import React from "react"
import { Redirect, Route, useHistory, useLocation, useRouteMatch } from "react-router-dom"
import { useQuery } from "@tanstack/react-query"
import { useIntl } from "react-intl"
import { Box, Tab, Tabs, Theme, useMediaQuery } from "@carrotfertility/carotene-core"
import type { MessagesHostContract } from "@internal/messages"
import { useProviderUnreadCount } from "@internal/messages/unread-dot"
import MemberMessagesContainer from "#/components/messaging"
import { MessagesHeader } from "#/components/messaging/MessagesHeader"
import { InAppMessagingContextProvider, useInAppMessaging } from "#/components/context/messaging/InAppMessagingContext"
import EmployeeOnlySpaceContainer from "#/components/employee-only-containers/EmployeeOnlySpaceContainer"
import { useUserRole } from "#/components/partner-access/hooks/usePartnerAccess"
import { EMAIL_VERIFICATION_CACHE_KEY } from "#/components/EmailVerification/useEmailVerification"
import { useCurrentUser } from "#/components/context/user/UserContext"
import { CenteredLoadingIndicator } from "#/components/views/molecules/Molecules"
import { PageLayout } from "#/features/global-ui/page-layout"
import { Switch } from "#/services/routing/Switch"
import { useMemberProgramFlags } from "#/services/user/hooks/useMemberProgram"
import { reportError, reportErrorMessage } from "#/utils/ErrorReporting"
import { providerApi } from "#/pages/consumer/messages/messagesHostApi"
import Settings from "#/utils/CarrotConfig"

// The messages URL grammar, stated once. `App.tsx` matches MESSAGES_PATH non-exactly and hands
// everything below it to this shell.
const MESSAGES_PATH = "/messages"
const THREAD_PATH = `${MESSAGES_PATH}/:thread`
// The Providers tab, which is a LITERAL and therefore has to be matched before `:thread` — see the
// inner Switch.
const PROVIDERS_PATH = `${MESSAGES_PATH}/providers`
// One segment for the whole conversation id. Healthie's ids are opaque and may contain hyphens, which
// a single segment tolerates; the package encodes anything that would not survive a path (see
// `formatThreadKey` there).
const PROVIDER_THREAD_PATH = `${PROVIDERS_PATH}/:thread`

// LAZY, so the Providers tab's panel — its transports, its react-query layer and its components — is
// not in the chunk a non-consumer downloads to read their care-team messages.
const ProviderMessagesPanel = React.lazy(() =>
  import("@internal/messages").then((m) => ({ default: m.ProviderMessagesPanel }))
)

// Every field is a module const that never changes identity — `providerApi` is built once per tab (see
// ./consumer/messages/messagesHostApi) and the reporters are module functions — so this is built once
// too rather than memoized per render. The runtime config is read here for the same reason: `window.env`
// is loaded before the bundle (see index.html), so it is settled by the time this module evaluates.
//
// ⛔ ONLY `true` IS ON, matching how the BFF reads its own copy of this variable
// (`HEALTHIE_MESSAGE_ATTACHMENTS_ENABLED` in routers/messages.ts): absent, `false`, `"TRUE"` and `1`
// are all off, and a gate is the one place where failing closed on a value nobody meant is right.
// Stringified first because /config.js is JAVASCRIPT rather than a process environment, so the same
// key arrives as a boolean from one deployment's file and as a string from another's. The package
// never reads host config itself; it takes the resolved boolean.
const providerHost: MessagesHostContract = {
  providerApi,
  reportError,
  reportErrorMessage,
  attachmentsEnabled: String(Settings.HEALTHIE_MESSAGE_ATTACHMENTS_ENABLED) === "true"
}

/**
 * The Providers tab's content, GATED ON THE FLAGS rather than on the route existing.
 *
 * The two provider routes are declared unconditionally, which is what makes this component the whole
 * of the gate — and the gate has three answers, not two:
 *
 *  - LOADING: wait. This is the flag race, and it is the one that silently breaks deep links: for the
 *    first frames after a page load `useMemberProgramFlags` has not answered, and a gate on
 *    `isConsumer` alone reads a consumer as a non-consumer and redirects them off their own link
 *    before the answer arrives. There is no way back from that — the redirect has already replaced the
 *    URL.
 *  - NOT A CONSUMER: an explicit `<Redirect>`, so the member lands on the page they do have rather than
 *    on this shell's chrome wrapped around nothing.
 *  - FAILED WITH NO ANSWER EVER: redirect, and not the same thing as loading. `isError` with no
 *    `data` means we asked and could not find out, which is settled; waiting on it would spin forever.
 *    Falling back to the Companion tab is falling back to what every member has. (A known follow-up
 *    recorded on CONAPP-185 is that four of five route shells collapse loading and error into one
 *    gate. This one does not.)
 *
 * There is deliberately NO fourth answer for `isError` on its own, and the omission is the decision:
 * react-query keeps the last successful `data` when a REFETCH fails, so a consumer whose background
 * refetch broke still reads as a consumer here and keeps their tab. Redirecting on `isError` regardless
 * would throw a member off their own conversation because a request they never made went wrong — and
 * a poll failure is the likeliest way for that to happen. So the condition is about the ANSWER, not
 * about the request: no usable answer redirects, a stale usable one is honoured.
 */
function ProvidersTabContent(): JSX.Element {
  const { data: programFlags, isLoading } = useMemberProgramFlags()

  if (isLoading) {
    return <CenteredLoadingIndicator />
  }
  if (programFlags?.isConsumer !== true) {
    return <Redirect to={MESSAGES_PATH} />
  }

  return (
    <React.Suspense fallback={<CenteredLoadingIndicator />}>
      <ProviderMessagesPanel host={providerHost} />
    </React.Suspense>
  )
}

/**
 * The tab bar, for a consumer and NOBODY ELSE.
 *
 * Rendered by this component rather than inline in the shell so that its provider-unread query sits
 * BELOW the shell's email-verification gate: that gate has to return before anything can mount a
 * messaging transport, and a hook called above it runs on the gated render too.
 *
 * A member with one messaging source gets no tab bar at all — not one tab, not a disabled one — and
 * that includes the frames before the flags answer. Appearing there would mean disappearing again for
 * every non-consumer, which is most members.
 *
 * `isConsumer` arrives as a PROP rather than being read from the flags here, because the same answer
 * decides whether the page header keeps its own rule — see `MessagesShell`. Two components deriving it
 * separately is how the bar and the rule drift apart.
 *
 * ⚠ IT ALSO CHECKS PARTNER ACCESS ITSELF, and that is load-bearing rather than belt-and-braces. This
 * renders in `PageLayout`'s HEADER slot — which is what puts the tabs directly under the page title —
 * and the header slot sits OUTSIDE `EmployeeOnlySpaceContainer`, the wrapper that replaces this page's
 * content with the member-only notice for a partner accessing the account. Without the check a partner
 * would get a working tab bar above that notice, and the Providers tab would switch a route under it.
 * The same reason covers `isUserRoleLoading`: the container renders nothing at all until the role is
 * known, so a bar that appeared first would flash and then be replaced by the notice.
 */
function MessagesTabs({ isConsumer, hidden }: { isConsumer: boolean; hidden: boolean }): JSX.Element | null {
  const intl = useIntl()
  const history = useHistory()
  const { isPartnerAccessingAccount, isUserRoleLoading } = useUserRole()

  // `undefined` for anyone but a consumer, which leaves the query disabled: provider unread state is
  // not consulted at all for a member who has no Providers tab to put a count on.
  const providerUnreadCount = useProviderUnreadCount(isConsumer ? providerApi.getThreads : undefined)

  const onProvidersTab = useRouteMatch({ path: PROVIDERS_PATH }) !== null
  const currentTab = onProvidersTab ? PROVIDERS_PATH : MESSAGES_PATH

  // `hidden` rather than the caller declining to render this: the tabs come and go as the member walks
  // in and out of a conversation, and returning null keeps the provider-unread query above MOUNTED
  // across that, instead of tearing it down and refetching on every pane switch.
  if (!isConsumer || isPartnerAccessingAccount || isUserRoleLoading || hidden) {
    return null
  }

  // CLINICIANS, from the design source (`Cash Pay - Messages.html`, `.msg-tabs .tab`). The route below
  // it is still `/messages/providers` and every internal name here still says "provider" — the label is
  // the only thing that changed, so no deep link breaks and nothing needs redirecting.
  const cliniciansLabel = intl.formatMessage({
    defaultMessage: "Clinicians",
    description: "tab holding the member's conversations with the clinicians treating them"
  })

  return (
    // Carotene's theme styles the bar's rule and its indicator, and this carries NO MARGIN of its own:
    // it renders in `PageLayout`'s header slot now, and the content box below already has a top margin.
    // A `marginBlockEnd` here would add to that rather than replace it — which is the gap that was asked
    // to be closed, arriving from the other side.
    <Tabs
      value={currentTab}
      // THE LABELS' TYPE, from the design source (`Cash Pay - Messages.html`, `.msg-tabs .tab`) rather
      // than from a screenshot. Carotene ships no `MuiTab` override, so MUI's defaults applied: both tabs
      // at one weight, and the selected label coloured `primary.main` — an orange label beneath an orange
      // indicator. The design gives the indicator that orange and the label a near-black.
      //
      // On the `Tabs` root rather than repeated on each `Tab`, so the two cannot drift and a third tab
      // would inherit it.
      sx={(theme) => ({
        "& .MuiTab-root": {
          // #5C5C5C, the design's `--wf-text-tertiary`, at the label's default weight.
          color: theme.palette.text.tertiary,
          "&:hover": {
            color: theme.palette.text.primary
          },
          "&.Mui-selected": {
            // #141414 and 700 — `--wf-text` and the design's own `font-weight` on the selected tab.
            color: theme.palette.text.primary,
            fontWeight: theme.typography.fontWeightBold
          }
        }
      })}
      aria-label={intl.formatMessage({
        defaultMessage: "Messages",
        description: "label for the tabs that switch between kinds of message"
      })}
      onChange={(_event, value: string) => {
        // MUI reports a click on the SELECTED tab too, and pushing then would add a history entry that
        // goes nowhere.
        if (value !== currentTab) history.push(value)
      }}
    >
      <Tab
        value={MESSAGES_PATH}
        label={intl.formatMessage({
          defaultMessage: "Carrot Companion",
          description: "tab holding the member's conversations with their Carrot care team"
        })}
      />
      <Tab
        value={PROVIDERS_PATH}
        // The count goes in the ACCESSIBLE NAME as well as on screen. The badge beside the label is
        // colour and position; this is the part a screen reader member gets.
        //
        // CONVERSATIONS, not messages, because that is what the number is: `useProviderUnreadCount`
        // filters threads by their `unread` flag, so a thread holding four unread replies contributes 1.
        // The label used to say "messages" and therefore under-reported every such thread — one unread
        // conversation with four replies in it announced as "1 unread message". It cannot be fixed from
        // the other end: `unread` is a BOOLEAN per thread all the way back through the BFF row
        // (`ProviderThreadList`), so there is no per-thread message count to sum, and the pill's digit
        // would have to change meaning to match the old noun rather than the other way round. The rest of
        // the app counts unread the same way — the Carrot thread's own flag is `hasUnreadMessages`.
        aria-label={
          providerUnreadCount > 0
            ? intl.formatMessage(
                {
                  defaultMessage:
                    "Clinicians, {count, plural, one {# unread conversation} other {# unread conversations}}",
                  description: "accessible name for the clinicians tab when it has unread conversations"
                },
                { count: providerUnreadCount }
              )
            : undefined
        }
        label={
          providerUnreadCount > 0 ? (
            // ONE ROW HOLDING BOTH, where these two used to be siblings in a fragment. MUI's `Tab` lays
            // its label out as a COLUMN, so the badge took a line of its own beneath "Providers" and
            // stretched the tab bar to fit — the design has it beside the word. An `inline-flex` row is
            // what puts them back on one line, and it does so whatever the parent's direction, which a
            // fragment cannot.
            // `gap` rather than a margin on the badge, so the pair has one source of separation — and
            // `spacing.xs` (8px) rather than the design's own 9px, which is not a spacing step. A
            // hard-coded length is not worth 1px here.
            <Box
              component="span"
              sx={(theme) => ({
                display: "inline-flex",
                alignItems: "center",
                gap: theme.spacing(theme.tokens.spacing.xs)
              })}
            >
              {cliniciansLabel}
              <Box
                component="span"
                data-testid="provider-unread-badge"
                sx={(theme) => ({
                  // A CIRCLE, not the oval this was. Side padding on an inline span sized the badge by
                  // its line height, which came out a third taller than the design's mark and wider
                  // still. The fixed block size and the matching floor on the inline size make a single
                  // digit round; the floor rather than a fixed width is what lets "12" widen it into a
                  // pill instead of clipping.
                  //
                  // THE DESIGN IS `.msg-tabs .tab-count` in `Cash Pay - Messages.html`:
                  // `min-width: 20px; height: 20px; padding: 0 6px; border-radius: 200px;
                  // font: 700 11px/20px; letter-spacing: 0.02em`. Everything with a token behind it takes
                  // the token, even where the design's own number differs by a pixel: `padding` is
                  // `spacing.xxs` (4px, against 6px) and the size below is `caption` (12px, against 11px).
                  //
                  // The 20px SQUARE is the one exception, and it is a literal on purpose: the steps either
                  // side are `md` (16px) and `lg` (24px), so the nearest token is 20% off the mark the
                  // design draws — 16px crowds the digit, 24px outweighs the label beside it. Same
                  // treatment as the nav dot's 7px and the marker track's 9px in `ProviderMessagesPanel`.
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  blockSize: "20px",
                  minInlineSize: "20px",
                  paddingInline: theme.spacing(theme.tokens.spacing.xxs),
                  borderRadius: theme.tokens.borderRadius.round,
                  // #F35B09 — `primary.accent`, the design's `--wf-tint-warm-text`. Deliberately NOT
                  // `primary.main` (#FE8D50): that is the orange of the tab INDICATOR two lines down, and
                  // the badge is the deeper of the two. They look interchangeable in a screenshot and are
                  // not in the source.
                  backgroundColor: theme.palette.primary.accent,
                  // WHITE, and NOT `primary.contrastText` — which reads like the right token beside a
                  // primary background and is #141414 in this theme, so it put a near-black digit on the
                  // orange circle. `secondary.contrastText` is the theme's #FFFFFF, and is how the rest
                  // of the app writes white text (see features/carrot-rx/**, features/ui/**).
                  color: theme.palette.secondary.contrastText,
                  // `caption` (12px) where the design says 11px — the nearest type step, and 1px is not
                  // worth a hard-coded size. The weight and the tracking have no token behind them, so
                  // they are the design's own: 700 through `fontWeightBold`, and 0.02em as written.
                  fontSize: theme.typography.caption.fontSize,
                  fontWeight: theme.typography.fontWeightBold,
                  letterSpacing: "0.02em",
                  // The design centres the digit with `line-height: 20px` against a 20px box, i.e. by
                  // filling it. This box centres with flex instead, so the line box must not also claim
                  // 20px — same result, one mechanism.
                  lineHeight: 1
                })}
              >
                {intl.formatNumber(providerUnreadCount)}
              </Box>
            </Box>
          ) : (
            cliniciansLabel
          )
        }
      />
    </Tabs>
  )
}

/**
 * Whether the tab bar takes itself off the page because a conversation is open in front of it.
 *
 * ONLY ON A SINGLE-COLUMN VIEWPORT. Below `lg` an open conversation REPLACES the threads list rather
 * than sitting beside it (both layouts hide one column at that breakpoint — `hideColumn` in the provider
 * panel, `hideColumn` in the Carrot container), so the member is one level down: the chrome above the
 * conversation is a back button and the thread's own subject, and a bar switching between two message
 * sources beneath that reads as a control over the conversation. From `lg` up the bar sits above two
 * visible columns and switches between two lists, which is exactly what it is for, so nothing changes.
 *
 * THE TWO TABS ANSWER "IS A CONVERSATION OPEN" FROM DIFFERENT PLACES, and they must not be read from the
 * same one:
 *
 *  - Providers: THE URL. Its panel derives the pane it shows from the route (`route.kind` there), so an
 *    open conversation is `/messages/providers/<key>`.
 *  - Companion: THE CONTEXT. Tapping a thread there sets `showOnlyThreadsList` and leaves the URL alone
 *    (see `src/components/messaging/index.tsx`) — the address bar still reads `/messages`. A rule
 *    derived from the route would never fire for the tab the request was about.
 *
 * Scoped to the tab in view for a reason that is easy to miss: BOTH selections live at once. A member who
 * opens a Carrot thread and then switches to Providers still has that thread open behind them, so reading
 * the Carrot context on the Providers tab would take the bar away from the provider threads list — along
 * with the way back to the conversation waiting behind it.
 *
 * The route-and-breakpoint half arrives as `providerConversationHeadsItself`, computed once in the shell,
 * because `PageLayout` needs the same answer for its header slot and cannot ask a component below itself.
 * Only the Companion half — the context — is read here.
 */
function useTabsHiddenOverConversation({
  onProvidersTab,
  providerConversationHeadsItself
}: {
  onProvidersTab: boolean
  providerConversationHeadsItself: boolean
}): boolean {
  const isSingleColumn = useMediaQuery((theme: Theme) => theme.breakpoints.down("lg"))
  const { showOnlyThreadsList } = useInAppMessaging()

  if (onProvidersTab) {
    return providerConversationHeadsItself
  }
  return isSingleColumn && !showOnlyThreadsList
}

/**
 * The page's chrome — its title and its tab bar — as ONE component, in `PageLayout`'s header slot.
 *
 * Together rather than side by side in the shell because the two are one decision twice over: the header
 * suppresses its own rule when the tab bar is there to draw one (`hideDivider`), and on the Providers tab
 * the title itself steps aside for exactly the conversations the bar does. Derived separately, the page
 * ends up with a bare title over nothing, or a rule with nothing above it.
 *
 * And it has to be BELOW `InAppMessagingContextProvider` to read the Carrot pane state at all, which the
 * shell — the component that renders that provider — cannot do.
 */
function MessagesPageChrome({
  hasTabBar,
  onProvidersTab,
  providerConversationHeadsItself
}: {
  hasTabBar: boolean
  onProvidersTab: boolean
  /**
   * An open provider conversation on a single-column viewport, which the whole of this chrome steps aside
   * for — see `MessagesShell`, which computes it because `PageLayout` needs the same answer.
   */
  providerConversationHeadsItself: boolean
}): JSX.Element {
  const tabsHiddenOverConversation = useTabsHiddenOverConversation({ onProvidersTab, providerConversationHeadsItself })

  return (
    <>
      {/* THE PAGE TITLE GOES WITH THE TABS, so one level down both tabs read the same way: the top of the
          screen is the conversation's own chrome — a back button, its date, who it is with — and nothing
          above it.
          Scoped to the PROVIDER side, and the asymmetry is about where each tab's thread chrome lives
          rather than about the design. The Companion tab's chrome IS this header — `MessagesHeader` swaps
          the title out for it (`Headers` there) — so there is nothing here to leave out. An open provider
          conversation draws its own inside the panel (`ThreadPaneHeader`), so the page title above it
          was a second header for the same thing: "Messages", a rule, and then the conversation's own
          heading beneath.
          `titleOnly` on the Providers tab, meanwhile, is not cosmetic either: `MessagesHeader` reads the
          selected thread out of the CARROT context and, on narrow viewports, titles the page with it —
          which on this tab would describe a conversation from the other one. */}
      {providerConversationHeadsItself ? null : (
        <MessagesHeader titleOnly={onProvidersTab} hideDivider={hasTabBar && !tabsHiddenOverConversation} />
      )}
      <MessagesTabs isConsumer={hasTabBar} hidden={tabsHiddenOverConversation} />
    </>
  )
}

/**
 * The shell every messages URL shares: the gate, the page chrome, the tab bar, and the route grammar
 * underneath.
 *
 * ONE PAGE SERVES EVERYONE. A consumer gets two tabs over the two messaging sources; everybody else
 * gets the page exactly as it was, with no tab bar at all.
 *
 * ONE MOUNT serves every Carrot URL in the grammar, which is the point — the context above the inner
 * `Switch` survives navigation between the threads list and a thread, so selection derives from the
 * URL (see `src/components/messaging/index.tsx`) instead of being mount-scoped state. The provider side
 * keeps its OWN context, inside `ProviderMessagesPanel`: two different context objects, so neither tab
 * can read or write the other's selection.
 */
export function MessagesShell(): JSX.Element {
  const { personalEmail } = useCurrentUser()
  const { state } = useLocation<{ previousPath: string }>()

  // ONE ANSWER, TWO CONSUMERS: whether the tab bar renders, and therefore whether the page header
  // keeps the rule under its title. The design's rule beneath the title is the TAB BAR's own bottom
  // rule (Carotene's theme draws it), so the header must drop its divider exactly when the bar is
  // there to replace it — and the bar is there for consumers only. Derived here rather than in each
  // component so the two cannot disagree and leave a member with two rules or none.
  //
  // Called above the email gate below, which costs nothing: `MainNavigation` already holds this query
  // open for every authenticated member, so this is a cache read rather than a request.
  const { data: programFlags } = useMemberProgramFlags()
  const hasTabBar = programFlags?.isConsumer === true

  // This page must NOT fetch the verification state — hence `enabled: false` and no query function.
  // `useEmailVerification` owns the request; this only subscribes to the answer. Subscribing rather
  // than reading the cache once is defensive: `AppLoadingContainer` blocks every authenticated child
  // until this query settles (`AppContainer.tsx:58`), so the answer is always cached before this
  // mounts, and subscribing keeps the gate correct if that shell gate is ever narrowed.
  const { data: isEmailVerified } = useQuery<boolean>([EMAIL_VERIFICATION_CACHE_KEY], { enabled: false })

  // Which pane a narrow viewport opens on, and which header the page gets. Matched here rather than
  // read with `useParams`, which would see nothing: the param belongs to the inner Switch, and the
  // context has to sit above it.
  //
  // `/messages/providers` MATCHES `/messages/:thread` — `:thread` is unconstrained — so the providers
  // match has to be subtracted from it. Without that, opening the Providers tab seeds the Carrot side
  // as though a thread were open: the narrow-viewport pane starts on the detail column, and
  // `MessagesHeader` titles the page with whatever Carrot thread is selected.
  const onProvidersTab = useRouteMatch({ path: PROVIDERS_PATH }) !== null
  const namesAThread = useRouteMatch({ path: THREAD_PATH, exact: true }) !== null && !onProvidersTab

  // An open provider conversation on a single-column viewport: the one state where the page's chrome —
  // title, rule and tab bar — steps aside entirely, because the conversation heads itself inside the panel.
  //
  // COMPUTED HERE, not inside the chrome, because `PageLayout` needs the same answer and cannot ask a
  // component it renders. With the slot empty, the space that separates chrome from content has nothing to
  // separate: leaving it in put this conversation's header 24px below the identical header on the Companion
  // tab, whose chrome IS the page's and so sits above that margin (measured in Chrome at 390×844, 60px
  // under the navigation against 36px).
  const isSingleColumn = useMediaQuery((theme: Theme) => theme.breakpoints.down("lg"))
  const onProviderThread = useRouteMatch({ path: PROVIDER_THREAD_PATH, exact: true }) !== null
  const providerConversationHeadsItself = isSingleColumn && onProviderThread

  // Must RETURN a `<Redirect>` before anything below can mount — never call `history.replace` from the
  // render body, which returns and lets the same pass mount the messaging transports for a member who
  // is not allowed to be here. Sitting ABOVE the inner Switch, this gates the whole grammar rather
  // than one URL in it.
  if (personalEmail !== null && !isEmailVerified) {
    return <Redirect to={{ pathname: state?.previousPath || "/", state: { showVerifyEmailModal: true } }} />
  }

  return (
    <InAppMessagingContextProvider initialShowOnlyThreadsList={!namesAThread}>
      {/* `fillViewport` on BOTH tabs. Each one's layout sizes its panes with `minmax(0, 1fr)`, which
          bounds nothing without a bounded ancestor: without it a long conversation grows the page and
          carries the composer off the bottom of the window, which is exactly what was reported here.
          The Companion tab keeps main's chrome and main's columns — only its SIZING is new. */}
      {/* THE TAB BAR RIDES IN THE HEADER SLOT, not at the top of the content, and that is what closes the
          gap between the page title and the tabs. `PageLayout` puts a `lg`/`xl` top margin on its content
          box, so a tab bar rendered as the first child sat that far below the title — roughly 32px of
          nothing between a heading and the control that belongs to it. In the header slot the tabs follow
          the title by `PageHeader`'s own 8px bottom padding, and the content margin then does the job it
          is for: separating the tabs from the panes below them. */}
      <PageLayout
        fillViewport
        headerSlotEmpty={providerConversationHeadsItself}
        header={
          <MessagesPageChrome
            hasTabBar={hasTabBar}
            onProvidersTab={onProvidersTab}
            providerConversationHeadsItself={providerConversationHeadsItself}
          />
        }
      >
        {/* `fillHeight` is the other half of `fillViewport` and neither works without the other: this
            wrapper renders a plain `<div>` by default, which takes its content's height and breaks the
            chain between the bound above and the grid below. Measured — without it the composer ends up
            clipped below the fold on a long thread. */}
        <EmployeeOnlySpaceContainer fillHeight>
          {/* Order is significant, twice over.

              The literal `providers` routes come FIRST because `:thread` is unconstrained and would
              otherwise match `/messages/providers` with `thread="providers"` — measured, not
              theoretical: the Providers tab rendered the Carrot container.

              The terminal `<Redirect>` matches everything, so it has to come last; ahead of the exact
              routes it would match the threads list itself and navigate to it forever. It is needed at
              all because `App.tsx`'s non-exact `/messages` shadows its `PageNotFound` fallback, and it
              replaces rather than pushes, so no back-button trap.

              `:thread` is deliberately UNCONSTRAINED. A one-segment key that names no thread the
              member has — `/messages/999`, `/messages/abc`, `/messages/102junk` — is absorbed by the
              container, which requires the key to spell a thread id in full, then drops it from the
              URL and opens the newest thread instead. A `(\d+)` constraint would move that into the
              router and force a catch-all to absorb it, for no gain, and it cannot be added halfway:
              an unconstrained `:thread` shadows any catch-all after it. */}
          <Switch>
            <Route exact path={PROVIDER_THREAD_PATH} component={ProvidersTabContent} />
            <Route exact path={PROVIDERS_PATH} component={ProvidersTabContent} />
            <Route exact path={THREAD_PATH} component={MemberMessagesContainer} />
            <Route exact path={MESSAGES_PATH} component={MemberMessagesContainer} />
            <Redirect to={MESSAGES_PATH} />
          </Switch>
        </EmployeeOnlySpaceContainer>
      </PageLayout>
    </InAppMessagingContextProvider>
  )
}
